/**
 * What a person can reach, and who decides.
 *
 * Three tiers, and they behave differently — conflating them is how a cashier
 * ends up looking at the activity log.
 *
 *   GRANTABLE SECTIONS  an owner flips a switch per staff member
 *   ADMIN AREAS         no switch exists at all; role decides, full stop
 *   EXTRA SWITCHES      not navigation — controls INSIDE a screen
 *
 * This lives in the shared package because both ends need the same answer. The
 * server enforces it in `SectionGuard`; the interface uses it to decide what to
 * render. If the two disagreed, the interface would offer something the API
 * refuses, and the bug would look like a broken screen rather than a
 * permissions error.
 *
 * The interface is NEVER the boundary. Everything here is about what to draw.
 */

/**
 * The eight an owner can grant. Each one is a place you can navigate to.
 *
 * Settings used to be one. Since Polishing 1.0 it holds only a person's own
 * things — profile, language, theme, help — so everybody reaches it, and the
 * pharmacy's own details are for owners. A `settings` key may still sit in
 * old stored access; nothing reads it.
 */
export const GRANTABLE_SECTIONS = [
  "pos",
  "products",
  "batches",
  "purchases",
  "transfers",
  "customers",
  "invoices",
  "reports",
] as const;

export type Section = (typeof GRANTABLE_SECTIONS)[number];

/**
 * The three that are never grantable.
 *
 * There is deliberately no switch for these — a section that cannot be granted
 * should not have a field that looks as though it could be. An owner who could
 * hand `users` to a cashier would be handing over the ability to grant
 * themselves everything else.
 */
export const ADMIN_AREAS = ["users", "billing", "audit"] as const;

export type AdminArea = (typeof ADMIN_AREAS)[number];

/** Anywhere the navigation can send somebody. */
export type Area = Section | AdminArea;

/**
 * The four that are not navigation.
 *
 * These hide controls within a screen a person can already reach: the cost
 * column on a product, the void button on a sale. Listed here so the set is
 * complete in one place, and so nobody mistakes one for a section.
 */
export const EXTRA_SWITCHES = [
  "seeCostPrice",
  "voidOrRefundSale",
  "giveDiscount",
  "sellOnCredit",
] as const;

export type ExtraSwitch = (typeof EXTRA_SWITCHES)[number];

export type Role = "admin" | "staff";

/**
 * Structural on purpose.
 *
 * The full `StaffAccess` shape lives with the database schema, which this
 * package must never import — the phone runs this code. Accepting anything
 * with the right booleans keeps the dependency pointing one way.
 */
export type AccessSet = Partial<Record<Section | ExtraSwitch, boolean>>;

export function isAdminArea(area: Area): area is AdminArea {
  return (ADMIN_AREAS as readonly string[]).includes(area);
}

/**
 * May this person reach this area?
 *
 * Admins get everything, including the three with no switch — which is why the
 * role check comes first and why nothing below it special-cases them. Staff get
 * exactly what their switches say, and an admin area is refused however many
 * switches they hold.
 */
export function canReach(
  area: Area,
  who: { role: Role; access: AccessSet },
): boolean {
  if (who.role === "admin") return true;
  if (isAdminArea(area)) return false;
  return who.access[area] === true;
}

/** The same question for a switch that hides a control rather than a section. */
export function canDo(
  action: ExtraSwitch,
  who: { role: Role; access: AccessSet },
): boolean {
  if (who.role === "admin") return true;
  return who.access[action] === true;
}

/** Every section this person may navigate to, in the declared order. */
export function reachableSections(who: { role: Role; access: AccessSet }): Section[] {
  return GRANTABLE_SECTIONS.filter((section) => canReach(section, who));
}
